<template>
    <section
        v-if="hasEnoughItems"
        :class="[
            'slice',
            'slice-reinsurance--with-columns',
            {
                'slice-reinsurance--with-columns--isMobile': slice.primary.hideInMobile,
                'slice-reinsurance--with-columns--reduce-spacing-top': slice.primary.reduceSpacing === 'top',
                'slice-reinsurance--with-columns--reduce-spacing-bottom': slice.primary.reduceSpacing === 'bottom',
                'slice-reinsurance--with-columns--reduce-spacing-all': slice.primary.reduceSpacing === 'all',
            },
        ]"
        data-testid="slice-reinsurance-with-columns"
    >
        <div class="slice-content">
            <PrismicRichText
                v-if="isFilled.richText(slice.primary.title)"
                :field="slice.primary.title"
                class="slice-title"
                data-testid="slice-reinsurance-title"
            />
            <ul
                :class="[
                    'slice-reinsurance--with-columns__columns',
                    {'slice-reinsurance--with-columns__columns--no-title': !isFilled.richText(slice.primary.title)},
                ]"
                data-testid="slice-reinsurance-columns"
            >
                <ReinsuranceColumn
                    v-for="(item, index) in columns"
                    :key="index"
                    :item="item"
                    :index="index"
                    :with-color="slice.primary.withColor === true"
                />
            </ul>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {ReinsuranceSlice} from '@prismicio-types';
    import ReinsuranceColumn from '../components/ReinsuranceColumn.vue';

    const MIN_REINSURANCE_ITEMS = 2;
    const MAX_REINSURANCE_ITEMS = 4;

    type ReinsuranceWithColumnsSlice = Extract<ReinsuranceSlice, {variation: 'reinsuranceWithColumns'}>;

    const {slice} = defineProps(getSliceComponentProps<ReinsuranceWithColumnsSlice>());

    const columns = computed(() => (slice.items ?? []).slice(0, MAX_REINSURANCE_ITEMS));
    const numberOfColumns = computed(() => columns.value.length);
    const hasEnoughItems = computed(() => (slice.items ?? []).length >= MIN_REINSURANCE_ITEMS);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-reinsurance {
        &--with-columns {
            overflow: hidden;

            &--isMobile {
                display: none;

                @include mq.screen_md(min) {
                    display: block;
                }
            }

            .slice-content {
                width: 100%;
                text-align: left;
                min-width: 0;
            }

            .slice-title {
                text-align: center;
            }

            &--reduce-spacing-top .slice-content,
            &--reduce-spacing-all .slice-content {
                padding-top: 0;
            }

            &--reduce-spacing-bottom .slice-content,
            &--reduce-spacing-all .slice-content {
                padding-bottom: 0;
            }

            &__columns {
                display: flex;
                gap: var(--joy-core-spacing-8);
                position: relative;
                width: 100%;
                overflow-x: auto;
                scroll-snap-type: x mandatory;
                -webkit-overflow-scrolling: touch;
                scrollbar-width: none;
                list-style: none;
                padding: 0;
                margin-top: var(--joy-core-spacing-8);

                &::-webkit-scrollbar {
                    display: none;
                }

                &--no-title {
                    padding-top: var(--joy-core-spacing-8);
                    border-top: 1px solid var(--joy-color-neutral-30);
                }

                @include mq.screen_sm(min) {
                    display: grid;
                    overflow: visible;
                    width: 100%;
                    gap: 32px;
                    grid-template-columns: repeat(v-bind(numberOfColumns), minmax(0, 1fr));
                }

                @include mq.screen_md(min) {
                    gap: 64px;
                }
            }
        }
    }
</style>
